export const JSON_FIELDS = ['ApplicabilityJson', 'ConditionsJson', 'ActionParametersJson'];
export const emptyAdvancedRule = () => ({
  Name: '', Description: '', IsEnabled: false, Priority: 100,
  EvaluationScope: 'EachPumpRow', ApplicabilityJson: '{"stationIds":[]}',
  ConditionsJson: '{"all":[]}', ActionType: 'Validate',
  ActionParametersJson: '{"operator":"eq","left":{"type":"constant","value":1},"right":{"type":"constant","value":1}}',
  Message: '', SchemaVersion: 1, Deleted: false,
});

export const normalizeAdvancedRule = (row) => Object.fromEntries(
  ['Id', ...Object.keys(emptyAdvancedRule())].map((key) => {
    const value = row[key] ?? row[key[0].toLowerCase() + key.slice(1)];
    return [key, JSON_FIELDS.includes(key) && value && typeof value === 'object'
      ? JSON.stringify(value, null, 2) : value ?? emptyAdvancedRule()[key]];
  })
);

export const parseRuleObject = (text, label) => {
  let value;
  try { value = JSON.parse(text); } catch { throw new Error(`${label} must contain valid JSON.`); }
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`${label} must be a JSON object.`);
  return value;
};

export const buildAdvancedRulePayload = (draft) => {
  if (!draft.Name.trim()) throw new Error('Enter a rule name.');
  if (!draft.EvaluationScope.trim() || !draft.ActionType.trim()) throw new Error('Choose an evaluation scope and action.');
  if (draft.Priority === '' || !Number.isInteger(Number(draft.Priority)) || Number(draft.Priority) < 0) throw new Error('Priority must be a whole number of zero or more.');
  if (!Number.isInteger(Number(draft.SchemaVersion)) || Number(draft.SchemaVersion) < 1) throw new Error('Schema version must be a positive whole number.');
  const payload = { ...draft, Name: draft.Name.trim(), Priority: Number(draft.Priority), SchemaVersion: Number(draft.SchemaVersion) };
  JSON_FIELDS.forEach(key => { payload[key] = JSON.stringify(parseRuleObject(draft[key], key)); });
  const applicability = JSON.parse(payload.ApplicabilityJson);
  ['stationIds', 'excludedStationIds'].forEach(key => {
    if (applicability[key] !== undefined && (!Array.isArray(applicability[key]) || applicability[key].some(id => !Number.isInteger(id) || id <= 0))) {
      throw new Error(`${key} must be a list of positive station IDs.`);
    }
  });
  const validateExpression = (value) => {
    if (!value || typeof value !== 'object') return;
    if (['fieldNumber', 'fieldValueId'].includes(value.type) && (!Number.isInteger(value.fieldId) || value.fieldId <= 0)) throw new Error('Choose a positive field ID for each field operand.');
    if (value.type === 'constant' && (value.value === '' || value.value === null || value.value === undefined)) throw new Error('Enter a value for each fixed operand.');
    Object.values(value).forEach(child => {
      if (Array.isArray(child)) child.forEach(validateExpression);
      else if (child && typeof child === 'object') validateExpression(child);
    });
  };
  validateExpression(JSON.parse(payload.ConditionsJson));
  const action = JSON.parse(payload.ActionParametersJson);
  validateExpression(action);
  if (payload.ActionType === 'SetFieldValue') {
    ['fieldId', 'fieldValueId'].forEach(key => {
      if (!Number.isInteger(action[key]) || action[key] <= 0) throw new Error('Set field value requires positive target field and value IDs.');
    });
  }
  payload.IsEnabled = [true, 1, 'true', '1'].includes(payload.IsEnabled);
  if (!payload.Id) delete payload.Id;
  return payload;
};

export const filterAdvancedRules = (rows, { search = '', status = '', scope = '', action = '' } = {}) => rows
  .filter(row => ![true, 1, 'true', '1'].includes(row.Deleted))
  .filter(row => !search || JSON.stringify(row).toLowerCase().includes(search.toLowerCase()))
  .filter(row => !status || ([true, 1, 'true', '1'].includes(row.IsEnabled) ? 'enabled' : 'disabled') === status)
  .filter(row => !scope || row.EvaluationScope === scope)
  .filter(row => !action || row.ActionType === action)
  .sort((a, b) => a.Priority - b.Priority || a.Name.localeCompare(b.Name));
